import React, { useRef, useState } from "react";

import classNames from "classnames";
import { Button, Carousel, CarouselItem, Form, Spinner } from "react-bootstrap";
import { FileUploader } from "react-drag-drop-files";
import { useTranslation } from "react-i18next";

import TableTemplate from "@components/Table/TableTemplate";
import { Languages } from "@constants/Languages";

import { DropdownList } from "react-widgets/cjs";

import "./ImportOverviewPage.scss";

/**
 * ImportOverviewPageView component renders a multi-step carousel interface for importing data.
 * It includes dropdowns for selecting import options and language, a file uploader for uploading CSV files,
 * and a table to display errors or results from the import process.
 *
 * @component
 * @param {Object} props - The properties passed to the component.
 * @param {number} props.index - The active index of the carousel.
 * @param {boolean} props.isLoading - Indicates whether the import process is loading.
 * @param {Object} props.returnData - The data returned from the import process.
 * @param {Array} props.returnData.data - The data rows returned from the import process.
 * @param {Array} props.returnData.errors - The errors returned from the import process.
 * @param {Array} props.errors - Additional errors to display.
 * @param {string} props.impOption - The currently selected import option.
 * @param {Array} props.importOptions - The list of available import options.
 * @param {string} props.languageCode - The currently selected language code.
 * @param {import("react").FormEventHandler<HTMLFormElement>} props.onCarouselFormSubmit - Callback function for form submission.
 * @param {import("react-widgets/esm/List").ChangeHandler<any>} props.onImportDropdownChange - Callback function for import dropdown change.
 * @param {import("react-widgets/esm/List").ChangeHandler<{ id: string; name: string; }>} props.onLanguageDropdownChange - Callback function for language dropdown change.
 * @param {Function} props.onFileUploaderChange - Callback function to set the uploaded file.
 * @param {import("react").MouseEventHandler<HTMLButtonElement>} props.onDownloadExampleButtonClick - Callback function for the back button click.
 * @param {import("react").MouseEventHandler<HTMLButtonElement>} props.onBackButtonClick - Callback function for the import button click.
 *
 * @returns {JSX.Element} The rendered ImportOverviewPageView component.
 */
function ImportOverviewPageView(props) {
    const [t] = useTranslation();
    const tableTemplateRef = useRef(null);

    const [columns] = useState([
        {
            name: "added",
            headerCaption: t("Added"),
            canSort: true,
            canFilter: true,
        },
        {
            name: "error",
            headerCaption: t("Error"),
            canSort: true,
            canFilter: true,
        },
        {
            name: "invalid",
            headerCaption: t("Invalid"),
            canSort: true,
            canFilter: true,
        },
        {
            name: "updated",
            headerCaption: t("Updated"),
            canSort: true,
            canFilter: true,
        },
        {
            name: "valid",
            headerCaption: t("Valid"),
            canSort: true,
            canFilter: true,
        },
        {
            name: "errorMessageKey",
            headerCaption: t("ErrorMessageKey"),
            canSort: true,
            canFilter: true,
        },
        {
            name: "errorType",
            headerCaption: t("errorType"),
            canSort: true,
            canFilter: true,
        },
        {
            name: "parameters.field",
            headerCaption: t("Field"),
            canSort: true,
            canFilter: true,
        },
        {
            name: "parameters.reason",
            headerCaption: t("Reason"),
            canSort: true,
            canFilter: true,
        },
    ]);

    return (
        <div className="importOverviewPage">
            <h1 className="importOverviewPage-title">{t("Import")}</h1>
            <Carousel
                className="importOverviewPage-carrousel"
                activeIndex={props.index}
                controls={false}
                touch={false}
                indicators={false}
                interval={1000}
            >
                <CarouselItem className="importOverviewPage-carrousel-1">
                    <Form onSubmit={props.onCarouselFormSubmit}>
                        <div className="importOverviewPage-carrousel-1-title">
                            <span>1.- {t("ImportStep1Title")}:</span>
                        </div>
                        {props.impOption && (
                            <DropdownList
                                className="importOverviewPage-carrousel-1-dropdown"
                                data={props.importOptions}
                                dataKey="id"
                                textField="name"
                                defaultValue={props.impOption}
                                onChange={props.onImportDropdownChange}
                            />
                        )}
                        {props.languageCode && (
                            <DropdownList
                                className="importOverviewPage-carrousel-1-dropdownLanguage"
                                data={Languages}
                                dataKey="id"
                                textField="name"
                                defaultValue={props.languageCode}
                                onChange={props.onLanguageDropdownChange}
                            />
                        )}

                        <div className="importOverviewPage-carrousel-1-title">
                            <span>2.- {t("ImportStep2Title")}:</span>
                        </div>
                        <Button
                            className="importOverviewPage-carrousel-1-download-btn"
                            onClick={props.onDownloadExampleButtonClick}
                            hidden={props.impOption === "translation"}
                        >
                            Download Example CSV
                        </Button>
                        <FileUploader
                            className="importOverviewPage-carrousel-1-inputFile"
                            handleChange={props.onFileUploaderChange}
                            name="file"
                            types={["csv"]}
                        />
                        <div className="importOverviewPage-carrousel-1-btns">
                            <Button
                                className={classNames("importOverviewPage-carrousel-1-btns-btn", {
                                    disabled: props.isLoading,
                                })}
                                type="submit"
                            >
                                {t("Send")}
                            </Button>
                            <div
                                className={classNames("importOverviewPage-carrousel-1-btns-spinner", {
                                    disableContent: !props.isLoading,
                                })}
                            >
                                <Spinner animation="border" role="status">
                                    <span className="visually-hidden">{t("Loading")}...</span>
                                </Spinner>
                            </div>
                        </div>
                    </Form>
                </CarouselItem>
                <CarouselItem className="importOverviewPage-carrousel-2">
                    <div className="importOverviewPage-carrousel-2-title">
                        {props.returnData.data && props.returnData.errors && props.errors && (
                            <div style={{ marginBottom: "40px" }}>
                                <TableTemplate
                                    // @ts-ignore
                                    dataSource={[props.returnData.data] || []}
                                    ref={tableTemplateRef}
                                    title={t("Errors")}
                                    columns={columns}
                                    uniqueId="added"
                                    paginator={false}
                                />
                            </div>
                        )}
                    </div>
                    <Button className="importOverviewPage-carrousel-2-btn" onClick={props.onBackButtonClick}>
                        {t("Back")}
                    </Button>
                </CarouselItem>
            </Carousel>
        </div>
    );
}

export default ImportOverviewPageView;
